import React from 'react';

import { useGetIntegrations } from 'hooks/domain/integrations/useGetIntegrations';
import { IntegrationKey } from 'resources/server/IntegrationsServer';

import { useShowRealsatisfied } from './useShowRealsatisfied';
import { useShowSellersShield } from './useShowSellersShield';
import { useShowUtilityConnect } from './useShowUtilityConnect';

/**
 * Custom hook that combines multiple integration dialog states into a single state
 * Handles prioritization between UtilityConnect, RealSatisfied, and SellersShield
 */
export function useShowIntegrationsDialog() {
  const utilityConnectState = useShowUtilityConnect();
  const realsatisfiedState = useShowRealsatisfied();
  const sellersShieldState = useShowSellersShield();
  const { data: integrations, refetch: refetchIntegrations } = useGetIntegrations();

  const promptEligibility = React.useMemo(() => {
    if (!integrations) {
      return {
        [IntegrationKey.UtilityConnect]: false,
        [IntegrationKey.RealSatisfied]: false,
        [IntegrationKey.SellersShield]: false,
      };
    }

    return {
      [IntegrationKey.UtilityConnect]: integrations.utilityconnect?.promptEligibility?.isEligible || false,
      [IntegrationKey.RealSatisfied]: integrations.realsatisfied?.promptEligibility?.isEligible || false,
      [IntegrationKey.SellersShield]: integrations.sellersshield?.promptEligibility?.isEligible || false,
    };
  }, [integrations]);

  return React.useMemo(() => {
    // Check if any integration should be shown
    const utilityShow = utilityConnectState.show && promptEligibility[IntegrationKey.UtilityConnect];
    const realsatisfiedShow = realsatisfiedState.show && promptEligibility[IntegrationKey.RealSatisfied];
    const sellersShieldShow = sellersShieldState.show && promptEligibility[IntegrationKey.SellersShield];

    // Determine which integrations to show
    const integrationsToShow: IntegrationKey[] = [];
    if (utilityShow) integrationsToShow.push(IntegrationKey.UtilityConnect);
    if (realsatisfiedShow) integrationsToShow.push(IntegrationKey.RealSatisfied);
    if (sellersShieldShow) integrationsToShow.push(IntegrationKey.SellersShield);

    // Set show based on any dialog being active
    const show = integrationsToShow.length > 0;

    // Combine loading states
    const isLoading = utilityConnectState.isLoading || realsatisfiedState.isLoading || sellersShieldState.isLoading;

    // Has shown if any of the integrated dialogs has shown
    const hasShown = utilityConnectState.hasShown || realsatisfiedState.hasShown || sellersShieldState.hasShown;

    return {
      show,
      isLoading,
      hasShown,
      integrationsToShow,
      promptEligibility,
      integrations,
      refetchIntegrations,
    };
  }, [
    utilityConnectState.show,
    utilityConnectState.isLoading,
    utilityConnectState.hasShown,
    realsatisfiedState.show,
    realsatisfiedState.isLoading,
    realsatisfiedState.hasShown,
    sellersShieldState.show,
    sellersShieldState.isLoading,
    sellersShieldState.hasShown,
    promptEligibility,
    integrations,
    refetchIntegrations,
  ]);
}
